import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* provider-doctor-data-store actions */
import * as ProviderDoctorDataStoreActions from 'src/app/features/data-stores/provider-doctor-data-store/state/provider-doctor-data-store.actions';

/* api services */
import { ProviderDoctorControllerService } from 'src/app/features/shared/sdk/services';

@Injectable()
export class ProviderDoctorDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private providerDoctorApiService: ProviderDoctorControllerService,
  ) { 
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start
    
    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end
    
    // #endregion customCode: data-store-effects__constructor__end
  }

  getProviderDoctors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDoctorDataStoreActions.GetProviderDoctors),
      mergeMap((action) => {
        return this.providerDoctorApiService.find().pipe(
          mergeMap((providerDoctors) => {
            const payload: any = {
              providerDoctors: providerDoctors,
            };
            return [
              ProviderDoctorDataStoreActions.GetProviderDoctorsSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [ProviderDoctorDataStoreActions.GetProviderDoctorsFailure()];
          }),
        );
      }),
    ),
  );

  getProviderDoctorById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDoctorDataStoreActions.GetProviderDoctorById),
      mergeMap((action) => {
        return this.providerDoctorApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((providerDoctor) => {
              const payload: any = {
                providerDoctor: providerDoctor,
              };
              return [
                ProviderDoctorDataStoreActions.GetProviderDoctorByIdSuccessful({
                  payload,
                }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                ProviderDoctorDataStoreActions.GetProviderDoctorByIdFailure(),
              ];
            }),
          );
      }),
    ),
  );

  createProviderDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDoctorDataStoreActions.CreateProviderDoctor),
      mergeMap((action) => {
        const params = {
          body: action.payload.providerDoctor,
        };
        return this.providerDoctorApiService.create(<any>params).pipe(
          mergeMap((providerDoctor) => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been saved successfully.',
            );
            const payload: any = {
              providerDoctor: providerDoctor,
            };
            return [
              ProviderDoctorDataStoreActions.CreateProviderDoctorSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [
              ProviderDoctorDataStoreActions.CreateProviderDoctorFailure(),
            ];
          }),
        );
      }),
    ),
  );

  updateProviderDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDoctorDataStoreActions.UpdateProviderDoctor),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: action.payload.data,
        };
        return this.providerDoctorApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Successful',
              'Your data has been updated successfully.',
            );
            return [ProviderDoctorDataStoreActions.GetProviderDoctors()];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [
              ProviderDoctorDataStoreActions.UpdateProviderDoctorFailure(),
            ];
          }),
        );
      }),
    ),
  );

  deleteProviderDoctor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProviderDoctorDataStoreActions.DeleteProviderDoctor),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.providerDoctorApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [
              ProviderDoctorDataStoreActions.DeleteProviderDoctorSuccessful({
                payload,
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [
              ProviderDoctorDataStoreActions.DeleteProviderDoctorFailure(),
            ];
          }),
        );
      }),
    ),
  );
}
